import { Component, OnInit, Input, signal, inject } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Params, QueryParamsHandling, Router } from '@angular/router';
import { PageEvent } from '@classi/spen';

import { ActivityRecord } from '@components/activity-record/activity-record';
import { MySpaceService } from '../my-space.service';
import { GoogleAnalytics4Service } from '@services/google-analytics-4.service';
import { RouterService } from '@services/router.service';
import { isMobileApp } from '@functions/browser';
import { MultipleFilterService } from '@services/filter/multiple-filter.service';

import { environment } from '@environments/environment';
import { isNil, omit, pickBy } from 'lodash';

@Component({
  selector: 'app-activity-records-list',
  templateUrl: './activity-records-list.component.html',
  styleUrls: ['./activity-records-list.component.scss'],
  providers: [MultipleFilterService],
  standalone: false,
})
export class ActivityRecordsListComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private mySpaceService = inject(MySpaceService);
  private analytics4Service = inject(GoogleAnalytics4Service);
  private routerService = inject(RouterService);
  filterService = inject(MultipleFilterService);

  @Input() unread_assessments_count;
  activityRecordLoaded = false;
  activityRecords: ActivityRecord[] = [];
  errMsg: string;
  readonly classiUrl = environment.platformUrl;
  private readonly disallowedUrlParamKeys = ['keyword'];
  readonly page = signal({
    per_page: 1,
    current_page: 1,
    next_page: 0,
    prev_page: 0,
    total_pages: 1,
    total_count: 0,
  });

  ngOnInit() {
    this.loadList(this.route.snapshot.queryParams);
    this.filterService.prepareData();
  }

  private async loadList(params: Params): Promise<void> {
    const queryParams = Object.assign({}, params);
    queryParams['page'] = +params['page'] || 1;
    await this.filterService.setFilter(queryParams);
    this.getActivityRecordsList(queryParams);
  }

  private navigate(params: Params, paramsHandling?: QueryParamsHandling): void {
    const apiParams = pickBy(params, (value) => !isNil(value));
    this.loadList(apiParams);

    const urlParams = omit(params, this.disallowedUrlParamKeys);
    const extras: NavigationExtras = { queryParams: urlParams, relativeTo: this.route, replaceUrl: true };
    if (paramsHandling !== undefined) {
      extras.queryParamsHandling = paramsHandling;
    }
    this.router.navigate(['.'], extras);
  }

  async getActivityRecordsList(params: object): Promise<void> {
    this.activityRecordLoaded = false;
    const response = await this.mySpaceService.getStudentActiviyRecordsList(params).toPromise();
    this.activityRecords = response.activity_records;
    this.page.set({ ...this.page(), ...response.pages });
    this.activityRecordLoaded = true;
  }

  getUnreadAssessments() {
    const params = Object.assign(this.filterService.getFilterData(), { has_unread_assessment: true });
    this.changeFilter(params);
  }

  get dataLoaded(): boolean {
    return this.activityRecordLoaded && this.filterService.categoriesLoaded;
  }

  get activityRecordsCount() {
    return this.page().total_count;
  }

  get showPagination(): boolean {
    return this.page().total_pages > 1;
  }

  pageChanged({ index }: PageEvent) {
    const params = { ...this.filterService.getFilterData(), page: index + 1 };
    this.navigate(params, 'merge');
    window.scrollTo(0, 0);
  }

  clickItem(activityRecord: ActivityRecord) {
    this.router.navigate(['/activity-records/student/', activityRecord.id]);
  }

  sendNewDocumentGAEvent() {
    this.analytics4Service.sendEvent('活動記録', '一覧画面', '活動記録の新規作成');
  }

  onBack() {
    this.routerService.goBack();
  }

  get isMobileApp() {
    return isMobileApp();
  }

  changeFilter(params: {}) {
    this.navigate(this.makeQueryParams(params));
  }

  removeCategoryFilter() {
    this.filterService.removeFilter();
    this.navigate(this.makeQueryParams({}));
  }

  makeQueryParams(params: {}) {
    return Object.assign(params, { tab: 'activities' });
  }
}
